import type { CreateApprovalLineRequest } from '$lib/api/types';

export type ApprovalLineFormValues = {
	name: string;
	description: string;
	steps: Array<{
		stepOrder: number;
		approverId: number;
		actionLabel: string;
		finalDecision?: boolean;
	}>;
};

export type ApprovalLineEmployeeOption = {
	id: number;
	name?: string | null;
};

type ValidationResult =
	| { ok: true; request: CreateApprovalLineRequest }
	| { ok: false; message: string };

export function validateApprovalLineForm(
	values: ApprovalLineFormValues,
	employees: ApprovalLineEmployeeOption[]
): ValidationResult {
	const name = values.name.trim();
	if (!name) {
		return { ok: false, message: '이름은 필수입니다.' };
	}
	if (name.length > 100) {
		return { ok: false, message: '이름은 100자 이하로 입력해주세요.' };
	}

	const description = values.description.trim();
	if (description.length > 500) {
		return { ok: false, message: '설명은 500자 이하로 입력해주세요.' };
	}

	if (values.steps.length === 0) {
		return { ok: false, message: '결재 단계를 최소 1개 이상 추가해주세요.' };
	}

	const selectableIds = new Set(employees.map((employee) => employee.id));
	const approverIds = values.steps.map((step) => Number(step.approverId));
	if (approverIds.some((approverId) => !Number.isInteger(approverId) || approverId <= 0)) {
		return { ok: false, message: '모든 단계의 결재자를 선택해주세요.' };
	}
	if (approverIds.some((approverId) => !selectableIds.has(approverId))) {
		return { ok: false, message: '선택할 수 없는 결재자가 포함되어 있습니다.' };
	}
	if (new Set(approverIds).size !== approverIds.length) {
		return { ok: false, message: '같은 결재자를 여러 단계에 중복 지정할 수 없습니다.' };
	}

	const actionLabels = values.steps.map((step) => normalizeActionLabel(step.actionLabel));
	if (actionLabels.some((label) => label.length > 20)) {
		return { ok: false, message: '승인 라벨은 20자 이하로 입력해주세요.' };
	}

	const request: CreateApprovalLineRequest = {
		name,
		steps: approverIds.map((approverId, index) => ({
			stepOrder: index + 1,
			approverId,
			actionLabel: actionLabels[index],
			finalDecision: Boolean(values.steps[index].finalDecision)
		}))
	};
	if (description) request.description = description;
	return { ok: true, request };
}

function normalizeActionLabel(value: string): string {
	const label = value.trim();
	return label || '승인';
}
